// Les événements de vie du prologue et de l'étape 1. Chaque événement est une
// carte avec deux ou trois choix ; le moteur (game/events.ts) les tire au sort.
// Les événements écrits par thèmes, avec leurs effets décrits simplement, sont dans eventPacks/.
import type { TextKey } from './fr';
import type { TextParams } from '../engine/i18n';
import { PACK_EVENTS } from './eventPacks';
import { MILIEUX } from './paths';
import { EXPENSES } from './expenses';
import { IMPULSE_ITEMS, type ImpulseItem } from './impulses';
import { futureValue, loanMonthlyPayment, round2 } from '../engine/finance';
import { fmtDate, fmtEur, fmtPct } from '../engine/format';
import { randomInt } from '../engine/rng';
import type { AssetId, EventOutcome, ExpenseId, GameState, PageId, StageId } from '../engine/state';
import { DAYS_PER_YEAR, dateAtDay } from '../engine/time';
import {
  addJournal,
  addLoan,
  addWellbeing,
  loseEnergy,
  payFromCash,
  payFromLivret,
  payFromSavings,
  scheduleFollowUp,
  unlockPage,
} from '../game/actions';
import {
  SCHOOL_FEE,
  atTodayPrices,
  deferredDebt,
  inPrivateSchool,
  livretOpen,
  monthlyIncome,
  monthlySpending,
  monthsUntilFirstJob,
  recordIncome,
} from '../game/economy';
import { enjoyPurchase, impulseBaseBonus } from '../game/treats';
import { CAR, buyCar, canBuyCar, carMonthlyCosts, carPrice, type CarKind } from '../game/car';
import { housingMonthly, moveOut, moveOutCost, moveToStudio, rentOf, studioSwitchCost } from '../game/housing';
import { depositToSavings, regulatedSavings } from '../game/livret';
import { cancelForgotten, forgottenTotal } from '../game/subscriptions';
import { renewUpgrade, upgradeAge } from '../game/upgrades';
import { avUcValue, hasMarketShares, sellUc } from '../game/av';
import { bankLoanRefusal, takeBankLoan } from '../game/bank';
import {
  DECOUVERT,
  depassement,
  dettesExigibles,
  excludeFromSchool,
  purchasePenalty,
  refuseConvocation,
  rejectionThreshold,
  restructureOverdraft,
  restructurePayment,
  startSurendettement,
} from '../game/decouvert';
import { coverOverdraftFromSavings } from '../game/decouvert';
import { changeOver, priceOf } from '../game/market';
import { buyShares, depositToPea, maxShares, peaHoldingsValue, sellShares } from '../game/pea';

export type EventKind =
  | 'imprevu'
  | 'chance'
  | 'etape'
  | 'tentation'
  | 'marche'
  | 'quotidien'
  | 'travail'
  | 'proches';
export type EventParams = Record<string, number>;
export type ChoiceResult = EventOutcome | 'scam';

export interface EventChoice {
  id: string;
  label: TextKey;
  /** Un choix peut n'apparaître que dans certaines situations. */
  visible?: (state: GameState, params: EventParams) => boolean;
  /** Une ligne sous le bouton, calculée : par exemple le découvert que ce choix entraînerait. */
  note?: (state: GameState, params: EventParams) => { key: TextKey; params?: TextParams } | null;
  available?: (state: GameState, params: EventParams) => boolean;
  unavailable?: TextKey | ((state: GameState, params: EventParams) => TextKey);
  apply: (state: GameState, params: EventParams) => ChoiceResult;
}

export interface EventDef {
  id: string;
  kind: EventKind;
  stages: StageId[];
  weight: number;
  /** Un coup dur : il respecte un délai minimum depuis le précédent. */
  bad?: boolean;
  /** Bien-être perdu sous le choc, dès que l'accident arrive. */
  shock?: number;
  maxCount?: number;
  /** Délai avant de pouvoir revenir, en jours de jeu (un an par défaut). */
  cooldownDays?: number;
  /** Les mois (1 à 12) où l'événement peut arriver ; il y pèse alors bien plus lourd au tirage. */
  season?: number[];
  /** Déclenché par une règle du jeu, jamais tiré au sort. */
  manual?: boolean;
  condition?: (state: GameState) => boolean;
  title: TextKey;
  text: TextKey;
  params?: (state: GameState) => EventParams;
  /** Page du carnet débloquée une fois l'événement réglé. */
  page?: PageId;
  choices: EventChoice[];
}

const PROJECTION_RATE = 0.07;
const PROJECTION_YEARS = 30;

function daysSinceFirstJob(state: GameState): number {
  return state.firstJobDay === null ? -1 : state.day - state.firstJobDay;
}

function setExpense(state: GameState, id: ExpenseId, amount: number): void {
  const expense = state.expenses[id];
  if (expense) expense.amount = amount;
  else state.expenses[id] = { amount, examined: 0, optimized: false };
}

/** Pas de travail en extra pendant un certain nombre de jours. */
function restFor(state: GameState, days: number): void {
  state.restUntilDay = Math.max(state.restUntilDay, state.day + days);
}

function loanOutcome(key: TextKey, payment: number, interest: number): EventOutcome {
  return { key, params: { payment: fmtEur(payment), interest: fmtEur(interest) } };
}

/** Le prix et le coût par mois d'une voiture : la bonne affaire d'un collègue, une occasion, ou une neuve. */
function carParams(kind: CarKind | 'bargain'): (state: GameState) => EventParams {
  return (state) => ({ price: carPrice(state, kind), monthly: carMonthlyCosts(state, kind === 'new' ? 'new' : 'used') });
}

/**
 * Les façons de payer une voiture : comptant, ou à crédit (quatre ans pour une occasion, cinq pour une neuve).
 * La bonne affaire d'un collègue garde aussi sa location avec option d'achat.
 */
function carChoices(event: string, kind: CarKind | 'bargain'): EventChoice[] {
  const key = (suffix: string) => `event.${event}.${suffix}` as TextKey;
  const model: CarKind = kind === 'new' ? 'new' : 'used';
  const choices: EventChoice[] = [
    {
      id: 'comptant',
      label: key('comptant'),
      available: (state, params) => Math.max(0, state.cash) + regulatedSavings(state) >= params.price,
      unavailable: 'event.notEnoughMoney',
      apply: (state, params) => {
        payFromSavings(state, params.price);
        buyCar(state, params.price, model);
        return { key: key('comptant.outcome') };
      },
    },
    {
      id: 'credit',
      label: key('credit'),
      available: (state) => !state.ficp,
      unavailable: 'event.ficpBloque',
      apply: (state, params) => {
        const label = model === 'new' ? 'loan.voitureNeuve' : 'loan.voiture';
        const loan = addLoan(state, label, params.price, 0.06, model === 'new' ? 60 : 48);
        buyCar(state, params.price, model);
        return loanOutcome(key('credit.outcome'), loan.monthlyPayment, loan.totalInterest);
      },
    },
  ];
  if (kind === 'bargain') {
    choices.push({
      id: 'loa',
      label: key('loa'),
      available: (state) => !state.ficp,
      unavailable: 'event.ficpBloque',
      apply: (state, params) => {
        const loan = addLoan(state, 'loan.loa', params.price, 0.09, 36);
        buyCar(state, params.price, 'used');
        return loanOutcome(key('loa.outcome'), loan.monthlyPayment, loan.totalInterest);
      },
    });
  }
  choices.push({ id: 'non', label: key('non'), apply: () => ({ key: key('non.outcome') }) });
  return choices;
}

/** Le prêt étudiant de la première rentrée : 15 000 € à 1,5 %, en différé total, remboursés sur cinq ans. */
export const STUDENT_LOAN = { amount: 15000, rate: 0.015, months: 60, smallJob: 120, job: 250 };

/** Ce que deviendra un prêt étudiant : sa dette au premier emploi, et la mensualité qui suivra. */
function studentLoanPlan(state: GameState, amount: number): { debt: number; payment: number } {
  const debt = deferredDebt(amount, STUDENT_LOAN.rate, monthsUntilFirstJob(state));
  return { debt: Math.round(debt), payment: Math.round(loanMonthlyPayment(debt, STUDENT_LOAN.rate, STUDENT_LOAN.months)) };
}

/** Les montants d'une carte, écrits en euros pour ses textes. */
function eurosOf(params: EventParams): Record<string, string> {
  return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, fmtEur(value)]));
}

/** Vend toutes les parts : l'argent reste dans le PEA, ou revient sur le fonds euros de l'assurance-vie. */
function sellAllShares(state: GameState): void {
  for (const asset of Object.keys(state.pea.holdings) as AssetId[]) {
    sellShares(state, asset, state.pea.holdings[asset]?.shares ?? 0);
  }
  for (const asset of Object.keys(state.av.holdings) as AssetId[]) {
    sellUc(state, asset, state.av.holdings[asset]?.shares ?? 0);
  }
}

/** Ce que valent les parts en bourse, au PEA et en unités de compte. */
function sharesValue(state: GameState): number {
  return round2(peaHoldingsValue(state) + avUcValue(state));
}

/** Verse sur le PEA puis achète des parts d'un placement, avec tout l'argent qui y attend. */
function investNow(state: GameState, asset: AssetId, deposit: number): void {
  depositToPea(state, deposit);
  buyShares(state, asset, maxShares(state, asset));
}

/** La baisse du marché depuis son plus haut (0,25 pour −25 %). */
function marketDrop(state: GameState): number {
  return 1 - state.market.index / state.market.indexPeak;
}

/** La première baisse : on garde ce que valaient les parts, pour le bilan d'un an après. */
function rememberDrop(state: GameState): void {
  state.pea.dropStudy = { value: sharesValue(state), index: state.market.index };
  scheduleFollowUp(state, 'premiere_baisse_bilan', DAYS_PER_YEAR, {});
}

/** Range l'argent qui dépasse un mois de dépenses : sur le Livret A s'il est ouvert (dans la limite du plafond), sinon dans la tirelire. */
function stashCash(state: GameState): void {
  const amount = round2(Math.max(0, state.cash - monthlySpending(state)));
  if (amount <= 0) return;
  if (livretOpen(state)) {
    const deposited = depositToSavings(state, amount);
    state.cash = round2(state.cash - deposited);
    state.year.saved += deposited;
  } else {
    state.cash = round2(state.cash - amount);
    state.piggy = round2(state.piggy + amount);
    unlockPage(state, 'se_payer_en_premier');
  }
}

/** Une envie d'achat inutile, déclenchée par l'argent gardé sous la main (voir game/temptations.ts). */
function impulseEvent(item: ImpulseItem): EventDef {
  return {
    id: `envie_${item.id}`,
    kind: 'tentation',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 0,
    manual: true,
    title: item.title,
    text: item.text,
    params: (state) => ({ price: Math.round(atTodayPrices(state, item.price) * purchasePenalty(state)), cash: Math.max(0, state.cash) }),
    page: 'loin_des_yeux',
    choices: [
      {
        id: 'acheter',
        label: 'envie.acheter',
        apply: (state, params) => {
          payFromCash(state, params.price);
          enjoyPurchase(state, impulseBaseBonus(item.price), 'achats');
          return { key: 'envie.acheter.outcome' };
        },
      },
      {
        id: 'attendre',
        label: 'envie.attendre',
        apply: (state) => {
          state.knowledgePoints += 1;
          return { key: 'envie.attendre.outcome' };
        },
      },
      {
        id: 'rangerTirelire',
        label: 'envie.rangerTirelire',
        visible: (state) => !livretOpen(state),
        apply: (state) => {
          stashCash(state);
          return { key: 'envie.ranger.outcome' };
        },
      },
      {
        id: 'rangerLivret',
        label: 'envie.rangerLivret',
        visible: (state) => livretOpen(state),
        apply: (state) => {
          stashCash(state);
          return { key: 'envie.ranger.outcome' };
        },
      },
    ],
  };
}

export const EVENTS: EventDef[] = [
  ...IMPULSE_ITEMS.map(impulseEvent),
  ...PACK_EVENTS,
  {
    id: 'forfait_telephone',
    kind: 'etape',
    stages: ['prologue', 'stage1'],
    weight: 3,
    maxCount: 1,
    condition: (state) => state.expenses.forfait !== undefined && !state.expenses.forfait.optimized,
    title: 'event.forfait_telephone.title',
    text: 'event.forfait_telephone.text',
    choices: [
      {
        id: 'cheap',
        label: 'event.forfait_telephone.cheap',
        apply: (state) => {
          const before = state.expenses.forfait?.amount ?? 20;
          state.expenses.forfait = { amount: 10, examined: 0, optimized: true, saved: Math.max(0, before - 10) };
          return { key: 'event.forfait_telephone.cheap.outcome' };
        },
      },
      {
        id: 'premium',
        label: 'event.forfait_telephone.premium',
        apply: (state) => {
          setExpense(state, 'forfait', 45);
          addWellbeing(state, 3);
          return { key: 'event.forfait_telephone.premium.outcome' };
        },
      },
    ],
  },
  {
    id: 'vacances_amis',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 2,
    maxCount: 1,
    title: 'event.vacances_amis.title',
    text: 'event.vacances_amis.text',
    params: (state) => ({ cost: atTodayPrices(state, 300) }),
    choices: [
      {
        id: 'partir',
        label: 'event.vacances_amis.partir',
        apply: (state, params) => {
          payFromCash(state, params.cost);
          addWellbeing(state, 8);
          return { key: 'event.vacances_amis.partir.outcome' };
        },
      },
      {
        id: 'court',
        label: 'event.vacances_amis.court',
        apply: (state) => {
          payFromCash(state, 120);
          addWellbeing(state, 4);
          return { key: 'event.vacances_amis.court.outcome' };
        },
      },
      {
        id: 'rester',
        label: 'event.vacances_amis.rester',
        apply: (state) => {
          addWellbeing(state, -3);
          return { key: 'event.vacances_amis.rester.outcome' };
        },
      },
    ],
  },
  {
    // La première rentrée de l'école privée (voir game/sim.ts) : comment payer les 5 000 € de chaque octobre.
    id: 'pret_etudiant',
    kind: 'etape',
    stages: ['prologue'],
    weight: 0,
    manual: true,
    maxCount: 1,
    condition: inPrivateSchool,
    title: 'event.pret_etudiant.title',
    text: 'event.pret_etudiant.text',
    params: (state) => {
      const full = studentLoanPlan(state, STUDENT_LOAN.amount);
      const half = studentLoanPlan(state, STUDENT_LOAN.amount / 2);
      return {
        fees: atTodayPrices(state, SCHOOL_FEE),
        amount: STUDENT_LOAN.amount,
        half: STUDENT_LOAN.amount / 2,
        debt: full.debt,
        payment: full.payment,
        halfPayment: half.payment,
        smallJob: STUDENT_LOAN.smallJob,
        job: STUDENT_LOAN.job,
      };
    },
    choices: [
      {
        id: 'emprunter',
        label: 'event.pret_etudiant.emprunter',
        available: (state) => !state.ficp,
        unavailable: 'event.ficpBloque',
        apply: (state, params) => {
          state.cash = round2(state.cash + params.amount);
          addLoan(state, 'loan.etudiant', params.amount, STUDENT_LOAN.rate, STUDENT_LOAN.months, true);
          return { key: 'event.pret_etudiant.emprunter.outcome', params: eurosOf(params) };
        },
      },
      {
        id: 'melanger',
        label: 'event.pret_etudiant.melanger',
        available: (state) => !state.ficp,
        unavailable: 'event.ficpBloque',
        apply: (state, params) => {
          state.cash = round2(state.cash + params.half);
          addLoan(state, 'loan.etudiant', params.half, STUDENT_LOAN.rate, STUDENT_LOAN.months, true);
          state.studentIncome += params.smallJob;
          addWellbeing(state, -4);
          return { key: 'event.pret_etudiant.melanger.outcome', params: eurosOf(params) };
        },
      },
      {
        id: 'travailler',
        label: 'event.pret_etudiant.travailler',
        apply: (state, params) => {
          state.studentIncome += params.job;
          addWellbeing(state, -8);
          return { key: 'event.pret_etudiant.travailler.outcome', params: eurosOf(params) };
        },
      },
    ],
  },
  {
    id: 'premiere_paie_alternance',
    kind: 'etape',
    stages: ['prologue'],
    weight: 0,
    manual: true,
    title: 'event.premiere_paie_alternance.title',
    text: 'event.premiere_paie_alternance.text',
    params: (state) => ({ amount: state.studentIncome + state.parentsHelp }),
    choices: [
      {
        id: 'repartir',
        label: 'event.premiere_paie_alternance.repartir',
        apply: (state) => {
          state.budget.autoSaving = 80;
          unlockPage(state, 'budget');
          return { key: 'event.premiere_paie_alternance.repartir.outcome' };
        },
      },
      {
        id: 'depenser',
        label: 'event.premiere_paie_alternance.depenser',
        apply: (state) => {
          payFromCash(state, 300);
          addWellbeing(state, 5);
          return { key: 'event.premiere_paie_alternance.depenser.outcome' };
        },
      },
    ],
  },
  {
    id: 'telephone_casse',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    weight: 3,
    bad: true,
    shock: 7,
    maxCount: 2,
    title: 'event.telephone_casse.title',
    text: 'event.telephone_casse.text',
    params: (state) => ({ cost: Math.round(Math.max(150, state.salary / 3)) }),
    page: 'fonds_urgence',
    choices: [
      {
        id: 'livret',
        label: 'event.telephone_casse.livret',
        available: (state, params) => regulatedSavings(state) >= params.cost,
        unavailable: 'event.notEnoughSavings',
        apply: (state, params) => {
          payFromLivret(state, params.cost);
          return { key: 'event.telephone_casse.livret.outcome' };
        },
      },
      {
        id: 'plusieursFois',
        label: 'event.telephone_casse.plusieursFois',
        apply: (state, params) => {
          const loan = addLoan(state, 'loan.telephone', params.cost, 0.12, 4);
          addWellbeing(state, -1);
          return loanOutcome('event.telephone_casse.plusieursFois.outcome', loan.monthlyPayment, loan.totalInterest);
        },
      },
      {
        id: 'carte',
        label: 'event.telephone_casse.carte',
        apply: (state, params) => {
          payFromCash(state, params.cost);
          if (state.cash < 0) addWellbeing(state, -3);
          return { key: 'event.telephone_casse.carte.outcome', params: { cash: fmtEur(state.cash) } };
        },
      },
    ],
  },
  {
    id: 'voiture_occasion',
    kind: 'etape',
    stages: ['stage1', 'stage2'],
    weight: 2,
    maxCount: 1,
    // Il faut le permis : sans lui, l'occasion ne se présente pas.
    condition: (state) => canBuyCar(state) && daysSinceFirstJob(state) >= 180,
    title: 'event.voiture_occasion.title',
    text: 'event.voiture_occasion.text',
    params: carParams('bargain'),
    choices: carChoices('voiture_occasion', 'bargain'),
  },
  {
    // La recherche lancée depuis le tableau de bord, quand on veut.
    id: 'recherche_voiture',
    kind: 'etape',
    stages: ['stage1', 'stage2'],
    weight: 0,
    manual: true,
    title: 'event.recherche_voiture.title',
    text: 'event.recherche_voiture.text',
    params: carParams('used'),
    choices: carChoices('recherche_voiture', 'used'),
  },
  {
    // Une voiture neuve, depuis le tableau de bord.
    id: 'voiture_neuve',
    kind: 'etape',
    stages: ['stage1', 'stage2'],
    weight: 0,
    manual: true,
    title: 'event.voiture_neuve.title',
    text: 'event.voiture_neuve.text',
    params: carParams('new'),
    choices: carChoices('voiture_neuve', 'new'),
  },
  {
    // Quitter le nid, depuis le tableau de bord : il faut de quoi s'installer.
    id: 'quitter_le_nid',
    kind: 'etape',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 0,
    manual: true,
    title: 'event.quitter_le_nid.title',
    text: 'event.quitter_le_nid.text',
    params: (state) => ({
      rentColoc: rentOf(state, 'coloc'),
      rentStudio: rentOf(state, 'studio'),
      totalColoc: housingMonthly(state, 'coloc'),
      totalStudio: housingMonthly(state, 'studio'),
      costColoc: moveOutCost(state, 'coloc'),
      costStudio: moveOutCost(state, 'studio'),
    }),
    choices: [
      {
        id: 'coloc',
        label: 'event.quitter_le_nid.coloc',
        available: (state, params) => Math.max(0, state.cash) + regulatedSavings(state) >= params.costColoc,
        unavailable: 'event.notEnoughMoney',
        apply: (state, params) => {
          payFromSavings(state, params.costColoc);
          moveOut(state, 'coloc');
          addWellbeing(state, 3);
          return { key: 'event.quitter_le_nid.coloc.outcome' };
        },
      },
      {
        id: 'studio',
        label: 'event.quitter_le_nid.studio',
        available: (state, params) => Math.max(0, state.cash) + regulatedSavings(state) >= params.costStudio,
        unavailable: 'event.notEnoughMoney',
        apply: (state, params) => {
          payFromSavings(state, params.costStudio);
          moveOut(state, 'studio');
          addWellbeing(state, 3);
          return { key: 'event.quitter_le_nid.studio.outcome' };
        },
      },
      {
        id: 'rester',
        label: 'event.quitter_le_nid.rester',
        apply: () => ({ key: 'event.quitter_le_nid.rester.outcome' }),
      },
    ],
  },
  {
    // Quitter la colocation pour un studio, depuis le tableau de bord : le dépôt de la chambre revient.
    id: 'demenager_studio',
    kind: 'etape',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 0,
    manual: true,
    condition: (state) => state.housing === 'coloc',
    title: 'event.demenager_studio.title',
    text: 'event.demenager_studio.text',
    params: (state) => ({
      rentStudio: rentOf(state, 'studio'),
      totalColoc: housingMonthly(state, 'coloc'),
      totalStudio: housingMonthly(state, 'studio'),
      deposit: rentOf(state, 'coloc'),
      cost: studioSwitchCost(state),
    }),
    choices: [
      {
        id: 'demenager',
        label: 'event.demenager_studio.demenager',
        available: (state, params) => Math.max(0, state.cash) + regulatedSavings(state) >= params.cost,
        unavailable: 'event.notEnoughMoney',
        apply: (state, params) => {
          payFromSavings(state, params.cost);
          moveToStudio(state);
          addWellbeing(state, 3);
          return { key: 'event.demenager_studio.demenager.outcome' };
        },
      },
      {
        id: 'rester',
        label: 'event.demenager_studio.rester',
        apply: () => ({ key: 'event.demenager_studio.rester.outcome' }),
      },
    ],
  },
  {
    // Avec une voiture, les offres d'emploi plus lointaines deviennent possibles.
    id: 'offre_emploi_voiture',
    kind: 'travail',
    stages: ['stage1', 'stage2'],
    weight: 2,
    maxCount: 1,
    condition: (state) => state.hasCar && daysSinceFirstJob(state) >= 365,
    title: 'event.offre_emploi_voiture.title',
    text: 'event.offre_emploi_voiture.text',
    params: (state) => ({ gain: Math.round(state.salary * CAR.jobRaise) }),
    choices: [
      {
        id: 'accepter',
        label: 'event.offre_emploi_voiture.accepter',
        apply: (state, params) => {
          state.salary = round2(state.salary * (1 + CAR.jobRaise));
          // Les premières semaines dans une nouvelle équipe fatiguent un peu.
          state.fatigueUntilDay = Math.max(state.fatigueUntilDay, state.day + 14);
          return { key: 'event.offre_emploi_voiture.accepter.outcome', params: { gain: fmtEur(params.gain) } };
        },
      },
      {
        id: 'rester',
        label: 'event.offre_emploi_voiture.rester',
        apply: () => ({ key: 'event.offre_emploi_voiture.rester.outcome' }),
      },
    ],
  },
  {
    id: 'black_friday',
    kind: 'tentation',
    stages: ['stage1', 'stage2'],
    // Un novembre sur deux environ, face à tous les autres événements.
    weight: 2,
    season: [11],
    cooldownDays: 300,
    maxCount: 3,
    title: 'event.black_friday.title',
    text: 'event.black_friday.text',
    // Le prix barré est gonflé : après la promotion, l'enceinte revient à un prix entre les deux.
    params: (state) => ({
      price: atTodayPrices(state, 249),
      reference: atTodayPrices(state, 450),
      later: atTodayPrices(state, 5 * randomInt(state, 56, 70)),
      used: atTodayPrices(state, 60),
    }),
    choices: [
      {
        id: 'acheter',
        label: 'event.black_friday.acheter',
        apply: (state, params) => {
          payFromCash(state, params.price);
          enjoyPurchase(state, impulseBaseBonus(params.price / state.priceLevel), 'achats');
          return { key: 'event.black_friday.acheter.outcome' };
        },
      },
      {
        id: 'attendre',
        label: 'event.black_friday.attendre',
        apply: (state, params) => {
          scheduleFollowUp(state, 'black_friday_apres', 2, params);
          return { key: 'event.black_friday.attendre.outcome' };
        },
      },
      {
        id: 'occasion',
        label: 'event.black_friday.occasion',
        apply: (state, params) => {
          payFromCash(state, params.used);
          enjoyPurchase(state, impulseBaseBonus(params.used / state.priceLevel), 'achats');
          return { key: 'event.black_friday.occasion.outcome' };
        },
      },
    ],
  },
  {
    // Deux jours après le Black Friday, pour qui a choisi d'attendre.
    id: 'black_friday_apres',
    kind: 'tentation',
    stages: ['stage1', 'stage2'],
    weight: 0,
    manual: true,
    title: 'event.black_friday_apres.title',
    text: 'event.black_friday_apres.text',
    choices: [
      {
        id: 'acheter',
        label: 'event.black_friday_apres.acheter',
        apply: (state, params) => {
          payFromCash(state, params.later);
          // Le plaisir tient à l'enceinte, pas au prix payé.
          enjoyPurchase(state, impulseBaseBonus(params.price / state.priceLevel), 'achats');
          return { key: 'event.black_friday_apres.acheter.outcome' };
        },
      },
      {
        id: 'renoncer',
        label: 'event.black_friday_apres.renoncer',
        apply: () => ({ key: 'event.black_friday_apres.renoncer.outcome' }),
      },
    ],
  },
  {
    id: 'prime_fin_annee',
    kind: 'chance',
    stages: ['stage1', 'stage2'],
    weight: 2,
    season: [12],
    cooldownDays: 300,
    maxCount: 3,
    condition: (state) => daysSinceFirstJob(state) >= 150,
    title: 'event.prime_fin_annee.title',
    text: 'event.prime_fin_annee.text',
    params: (state) => ({ amount: Math.round(state.salary) }),
    choices: [
      {
        id: 'depenser',
        label: 'event.prime_fin_annee.depenser',
        apply: (state, params) => {
          // La prime est reçue, puis dépensée : elle compte dans les revenus de l'année.
          recordIncome(state, params.amount);
          // Voulu : le plaisir de la prime ne s'use pas, contrairement aux achats plaisir.
          addWellbeing(state, 8);
          const fv = futureValue(params.amount, PROJECTION_RATE, PROJECTION_YEARS);
          return { key: 'event.prime_fin_annee.depenser.outcome', params: { fv: fmtEur(fv) } };
        },
      },
      {
        id: 'placer',
        label: 'event.prime_fin_annee.placer',
        apply: (state, params) => {
          recordIncome(state, params.amount);
          // Ce qui ne rentre plus sous le plafond du Livret A reste sur le compte courant.
          const deposited = depositToSavings(state, params.amount);
          state.cash = round2(state.cash + params.amount - deposited);
          state.year.saved += deposited;
          const fv = futureValue(params.amount, PROJECTION_RATE, PROJECTION_YEARS);
          return { key: 'event.prime_fin_annee.placer.outcome', params: { fv: fmtEur(fv) } };
        },
      },
      {
        id: 'moitie',
        label: 'event.prime_fin_annee.moitie',
        apply: (state, params) => {
          recordIncome(state, params.amount);
          const half = params.amount / 2;
          const deposited = depositToSavings(state, half);
          state.cash = round2(state.cash + half - deposited);
          state.year.saved += deposited;
          addWellbeing(state, 4);
          const fv = futureValue(half, PROJECTION_RATE, PROJECTION_YEARS);
          return { key: 'event.prime_fin_annee.moitie.outcome', params: { fv: fmtEur(fv) } };
        },
      },
    ],
  },
  {
    id: 'abonnements_oublies',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    weight: 2,
    maxCount: 1,
    condition: (state) =>
      state.expenses.abonnements !== undefined &&
      !state.expenses.abonnements.optimized &&
      forgottenTotal(state) > 0 &&
      daysSinceFirstJob(state) >= 60,
    title: 'event.abonnements_oublies.title',
    text: 'event.abonnements_oublies.text',
    params: (state) => ({ amount: forgottenTotal(state) }),
    choices: [
      {
        id: 'trier',
        label: 'event.abonnements_oublies.trier',
        apply: (state, params) => {
          const expense = state.expenses.abonnements;
          if (expense) {
            const before = expense.amount;
            cancelForgotten(state);
            expense.optimized = true;
            expense.examined = EXPENSES.abonnements.hunt?.clicks ?? 0;
            expense.saved = round2(before - expense.amount);
          }
          return { key: 'event.abonnements_oublies.trier.outcome', params: { amount: fmtEur(params.amount) } };
        },
      },
      {
        id: 'laisser',
        label: 'event.abonnements_oublies.laisser',
        apply: () => ({ key: 'event.abonnements_oublies.laisser.outcome' }),
      },
    ],
  },
  {
    id: 'mariage_ami',
    kind: 'etape',
    stages: ['stage1', 'stage2'],
    weight: 1,
    maxCount: 1,
    title: 'event.mariage_ami.title',
    text: 'event.mariage_ami.text',
    params: (state) => ({
      low: atTodayPrices(state, 150),
      mid: atTodayPrices(state, 350),
      high: atTodayPrices(state, 700),
    }),
    choices: [
      {
        id: 'serre',
        label: 'event.mariage_ami.serre',
        apply: (state, params) => {
          payFromCash(state, params.low);
          return { key: 'event.mariage_ami.outcome' };
        },
      },
      {
        id: 'normal',
        label: 'event.mariage_ami.normal',
        apply: (state, params) => {
          payFromCash(state, params.mid);
          addWellbeing(state, 3);
          return { key: 'event.mariage_ami.outcome' };
        },
      },
      {
        id: 'grand',
        label: 'event.mariage_ami.grand',
        apply: (state, params) => {
          payFromCash(state, params.high);
          addWellbeing(state, 5);
          return { key: 'event.mariage_ami.outcome' };
        },
      },
    ],
  },
  {
    id: 'mutation',
    kind: 'etape',
    stages: ['stage1', 'stage2'],
    weight: 1,
    maxCount: 1,
    // Un propriétaire ne déménage pas sur un coup de tête : il faudrait vendre.
    condition: (state) => daysSinceFirstJob(state) >= 365 && state.housing !== 'proprio',
    title: 'event.mutation.title',
    text: 'event.mutation.text',
    params: (state) => ({ cost: atTodayPrices(state, 1500) }),
    choices: [
      {
        id: 'demenager',
        label: 'event.mutation.demenager',
        apply: (state, params) => {
          state.salary = round2(state.salary * 1.1);
          payFromCash(state, params.cost);
          addWellbeing(state, -4);
          unlockPage(state, 'train_de_vie');
          return { key: 'event.mutation.demenager.outcome', params: { salary: fmtEur(state.salary) } };
        },
      },
      {
        id: 'rester',
        label: 'event.mutation.rester',
        apply: (state) => {
          addWellbeing(state, 2);
          return { key: 'event.mutation.rester.outcome' };
        },
      },
    ],
  },
  {
    id: 'placement_miracle',
    kind: 'tentation',
    stages: ['stage1', 'stage2'],
    weight: 2,
    maxCount: 1,
    condition: (state) => daysSinceFirstJob(state) >= 90,
    title: 'event.placement_miracle.title',
    text: 'event.placement_miracle.text',
    params: (state) => ({ amount: atTodayPrices(state, 1000) }),
    page: 'arnaques',
    choices: [
      {
        id: 'investir',
        label: 'event.placement_miracle.investir',
        available: (state, params) => Math.max(0, state.cash) + regulatedSavings(state) >= params.amount,
        unavailable: 'event.notEnoughMoney',
        apply: (state, params) => {
          payFromSavings(state, params.amount);
          addWellbeing(state, -8);
          return { key: 'event.placement_miracle.investir.outcome' };
        },
      },
      {
        id: 'ignorer',
        label: 'event.placement_miracle.ignorer',
        apply: () => ({ key: 'event.placement_miracle.ignorer.outcome' }),
      },
      {
        id: 'analyser',
        label: 'event.placement_miracle.analyser',
        apply: () => 'scam',
      },
    ],
  },
  {
    id: 'ordinateur_panne',
    kind: 'imprevu',
    stages: ['prologue'],
    weight: 3,
    bad: true,
    shock: 9,
    maxCount: 1,
    // L'ordinateur de la boutique est garanti deux ans : avant, une panne est réparée sans frais.
    condition: (state) =>
      !state.upgrades.includes('ordinateur') || upgradeAge(state, 'ordinateur') >= 2 * DAYS_PER_YEAR,
    title: 'event.ordinateur_panne.title',
    text: 'event.ordinateur_panne.text',
    params: () => ({ cost: 600, low: 250 }),
    page: 'fonds_urgence',
    // Quel que soit le choix, l'ordinateur remplacé repart pour toute sa durée de vie.
    choices: [
      {
        id: 'livret',
        label: 'event.ordinateur_panne.livret',
        available: (state, params) => regulatedSavings(state) >= params.cost,
        unavailable: 'event.notEnoughSavings',
        apply: (state, params) => {
          payFromLivret(state, params.cost);
          renewUpgrade(state, 'ordinateur');
          return { key: 'event.ordinateur_panne.livret.outcome' };
        },
      },
      {
        id: 'occasion',
        label: 'event.ordinateur_panne.occasion',
        apply: (state, params) => {
          payFromCash(state, params.low);
          renewUpgrade(state, 'ordinateur');
          return { key: 'event.ordinateur_panne.occasion.outcome' };
        },
      },
      {
        id: 'credit',
        label: 'event.ordinateur_panne.credit',
        apply: (state, params) => {
          const loan = addLoan(state, 'loan.ordinateur', params.cost, 0.15, 10);
          renewUpgrade(state, 'ordinateur');
          addWellbeing(state, -1);
          return loanOutcome('event.ordinateur_panne.credit.outcome', loan.monthlyPayment, loan.totalInterest);
        },
      },
    ],
  },
  {
    // La bourse sur critères sociaux : la réponse arrive avec la première rentrée (voir game/sim.ts).
    id: 'bourse_etudes',
    kind: 'chance',
    stages: ['prologue'],
    weight: 0,
    manual: true,
    maxCount: 1,
    condition: (state) => inPrivateSchool(state) && state.milieu !== 'aise',
    title: 'event.bourse_etudes.title',
    text: 'event.bourse_etudes.text',
    params: (state) => ({ amount: MILIEUX[state.milieu].scholarship }),
    choices: [
      {
        id: 'epargner',
        label: 'event.bourse_etudes.epargner',
        apply: (state, params) => {
          state.studentIncome += params.amount;
          state.budget.autoSaving += 100;
          return { key: 'event.bourse_etudes.epargner.outcome' };
        },
      },
      {
        id: 'profiter',
        label: 'event.bourse_etudes.profiter',
        apply: (state, params) => {
          state.studentIncome += params.amount;
          addWellbeing(state, 5);
          return { key: 'event.bourse_etudes.profiter.outcome' };
        },
      },
    ],
  },
  {
    id: 'bourse_merite',
    kind: 'chance',
    stages: ['prologue'],
    weight: 2,
    maxCount: 1,
    condition: (state) => state.path === 'studies' && state.milieu === 'aise',
    title: 'event.bourse_merite.title',
    text: 'event.bourse_merite.text',
    params: () => ({ amount: 150 }),
    choices: [
      {
        id: 'epargner',
        label: 'event.bourse_merite.epargner',
        apply: (state, params) => {
          state.studentIncome += params.amount;
          state.budget.autoSaving += 100;
          return { key: 'event.bourse_merite.epargner.outcome' };
        },
      },
      {
        id: 'profiter',
        label: 'event.bourse_merite.profiter',
        apply: (state, params) => {
          state.studentIncome += params.amount;
          addWellbeing(state, 5);
          return { key: 'event.bourse_merite.profiter.outcome' };
        },
      },
    ],
  },
  {
    id: 'stage_ete',
    kind: 'etape',
    stages: ['prologue'],
    weight: 3,
    maxCount: 1,
    condition: (state) => state.path === 'studies',
    title: 'event.stage_ete.title',
    text: 'event.stage_ete.text',
    params: () => ({ pay: 1200 }),
    choices: [
      {
        id: 'stage',
        label: 'event.stage_ete.stage',
        apply: (state, params) => {
          state.cash = round2(state.cash + params.pay);
          // L'expérience s'ajoute à la compétence travaillée ; les niveaux se calculent au jour suivant.
          state.skills[state.focusSkill].xp += 60;
          addWellbeing(state, -2);
          return { key: 'event.stage_ete.stage.outcome' };
        },
      },
      {
        id: 'vacances',
        label: 'event.stage_ete.vacances',
        apply: (state) => {
          payFromCash(state, 300);
          addWellbeing(state, 8);
          return { key: 'event.stage_ete.vacances.outcome' };
        },
      },
      {
        id: 'job',
        label: 'event.stage_ete.job',
        apply: (state) => {
          state.cash = round2(state.cash + 900);
          addWellbeing(state, -3);
          return { key: 'event.stage_ete.job.outcome' };
        },
      },
    ],
  },
  {
    id: 'soiree_etudiante',
    kind: 'tentation',
    stages: ['prologue'],
    weight: 2,
    maxCount: 3,
    title: 'event.soiree_etudiante.title',
    text: 'event.soiree_etudiante.text',
    params: () => ({ cost: 25 }),
    choices: [
      {
        id: 'aller',
        label: 'event.soiree_etudiante.aller',
        apply: (state, params) => {
          payFromCash(state, params.cost + 40);
          addWellbeing(state, 4);
          // Une nuit courte : le lendemain, l'énergie manque.
          loseEnergy(state, 5);
          return { key: 'event.soiree_etudiante.aller.outcome' };
        },
      },
      {
        id: 'amis',
        label: 'event.soiree_etudiante.amis',
        apply: (state) => {
          payFromCash(state, 10);
          addWellbeing(state, 3);
          loseEnergy(state, 2);
          return { key: 'event.soiree_etudiante.amis.outcome' };
        },
      },
      {
        id: 'rester',
        label: 'event.soiree_etudiante.rester',
        apply: (state) => {
          addWellbeing(state, -2);
          return { key: 'event.soiree_etudiante.rester.outcome' };
        },
      },
    ],
  },
  {
    id: 'manuels',
    kind: 'chance',
    stages: ['prologue'],
    weight: 2,
    maxCount: 1,
    condition: (state) => state.path !== 'work',
    title: 'event.manuels.title',
    text: 'event.manuels.text',
    params: () => ({ amount: 120 }),
    choices: [
      {
        id: 'vendre',
        label: 'event.manuels.vendre',
        apply: (state, params) => {
          state.cash = round2(state.cash + params.amount);
          return { key: 'event.manuels.vendre.outcome' };
        },
      },
      {
        id: 'donner',
        label: 'event.manuels.donner',
        apply: (state) => {
          addWellbeing(state, 3);
          return { key: 'event.manuels.donner.outcome' };
        },
      },
      {
        id: 'garder',
        label: 'event.manuels.garder',
        apply: () => ({ key: 'event.manuels.garder.outcome' }),
      },
    ],
  },
  {
    id: 'coup_de_blues',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 0,
    manual: true,
    title: 'event.coup_de_blues.title',
    text: 'event.coup_de_blues.text',
    params: (state) => ({ cost: atTodayPrices(state, 60) }),
    choices: [
      {
        id: 'reposer',
        label: 'event.coup_de_blues.reposer',
        apply: (state) => {
          restFor(state, 30);
          addWellbeing(state, 5);
          return { key: 'event.coup_de_blues.reposer.outcome' };
        },
      },
      {
        id: 'sortir',
        label: 'event.coup_de_blues.sortir',
        apply: (state, params) => {
          restFor(state, 30);
          payFromCash(state, params.cost);
          addWellbeing(state, 8);
          return { key: 'event.coup_de_blues.sortir.outcome' };
        },
      },
      {
        id: 'suzanne',
        label: 'event.coup_de_blues.suzanne',
        apply: (state) => {
          restFor(state, 30);
          addWellbeing(state, 4);
          return { key: 'event.coup_de_blues.suzanne.outcome' };
        },
      },
    ],
  },
  {
    id: 'epuisement',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 0,
    manual: true,
    title: 'event.epuisement.title',
    text: 'event.epuisement.text',
    choices: [
      {
        id: 'recuperer',
        label: 'event.epuisement.recuperer',
        apply: (state) => {
          restFor(state, 90);
          state.promotionDelay += 90;
          addWellbeing(state, 5);
          return { key: 'event.epuisement.recuperer.outcome' };
        },
      },
    ],
  },
  {
    id: 'fatigue',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    weight: 0,
    bad: true,
    shock: 8,
    manual: true,
    title: 'event.fatigue.title',
    text: 'event.fatigue.text',
    choices: [
      {
        id: 'reposer',
        label: 'event.fatigue.reposer',
        apply: (state) => {
          state.restUntilDay = state.day + 30;
          addWellbeing(state, 5);
          const date = fmtDate(dateAtDay(state.startDate, state.restUntilDay));
          return { key: 'event.fatigue.reposer.outcome', params: { date } };
        },
      },
      {
        id: 'continuer',
        label: 'event.fatigue.continuer',
        apply: (state) => {
          state.fatigueUntilDay = state.day + 90;
          addWellbeing(state, -10);
          return { key: 'event.fatigue.continuer.outcome' };
        },
      },
    ],
  },

  // La spirale du découvert (voir game/decouvert.ts) : ses cartes arrivent par les suites programmées.
  ...spiralCards(),
  ...schoolUnpaidCards(),

  // La bourse fictive, à l'étape 2.
  {
    // La première baisse garantie, un à deux ans après le premier achat (voir game/market.ts).
    id: 'premiere_baisse',
    kind: 'marche',
    stages: ['stage2'],
    weight: 0,
    manual: true,
    title: 'event.premiere_baisse.title',
    text: 'event.premiere_baisse.text',
    params: (state) => {
      const drop = marketDrop(state);
      const value = sharesValue(state);
      return { dropPct: drop, value, lost: Math.round((value * drop) / Math.max(0.01, 1 - drop)) };
    },
    choices: [
      {
        id: 'vendre',
        label: 'event.premiere_baisse.vendre',
        apply: (state) => {
          rememberDrop(state);
          sellAllShares(state);
          return { key: 'event.premiere_baisse.vendre.outcome' };
        },
      },
      {
        id: 'garder',
        label: 'event.premiere_baisse.garder',
        apply: (state) => {
          rememberDrop(state);
          state.flags.heldThroughDrop = true;
          return { key: 'event.premiere_baisse.garder.outcome' };
        },
      },
      {
        id: 'acheter',
        label: 'event.premiere_baisse.acheter',
        // L'argent qui attend d'être placé : celui du PEA.
        visible: (state) => state.pea.open,
        apply: (state) => {
          rememberDrop(state);
          state.flags.heldThroughDrop = true;
          investNow(state, state.pea.monthlyAsset, 0);
          return { key: 'event.premiere_baisse.acheter.outcome' };
        },
      },
    ],
  },
  {
    // Un an après la première baisse : ce que seraient devenues les parts, gardées ou vendues.
    id: 'premiere_baisse_bilan',
    kind: 'marche',
    stages: ['stage2'],
    weight: 0,
    manual: true,
    title: 'event.premiere_baisse_bilan.title',
    text: 'event.premiere_baisse_bilan.text',
    params: (state) => {
      const study = state.pea.dropStudy ?? { value: 0, index: state.market.index };
      const ratio = study.index > 0 ? state.market.index / study.index : 1;
      return { changePct: ratio - 1, kept: Math.round(study.value * ratio), sold: Math.round(study.value) };
    },
    choices: [
      {
        id: 'fermer',
        label: 'event.premiere_baisse_bilan.fermer',
        apply: () => ({ key: 'event.premiere_baisse_bilan.fermer.outcome' }),
      },
    ],
  },
  {
    id: 'fonds_maison_conseiller',
    kind: 'tentation',
    stages: ['stage2'],
    weight: 2,
    maxCount: 1,
    condition: (state) => state.pea.open && state.cash >= 1000,
    title: 'event.fonds_maison_conseiller.title',
    text: 'event.fonds_maison_conseiller.text',
    // Sur trente ans, environ 7,2 % par an pour le fonds indiciel contre 5,4 % pour le fonds de la banque.
    params: () => ({ amount: 1000, gap: Math.round(1000 * (1.072 ** 30 - 1.054 ** 30)) }),
    choices: [
      {
        id: 'suivre',
        label: 'event.fonds_maison_conseiller.suivre',
        apply: (state, params) => {
          investNow(state, 'maison', params.amount);
          return { key: 'event.fonds_maison_conseiller.suivre.outcome' };
        },
      },
      {
        id: 'indiciel',
        label: 'event.fonds_maison_conseiller.indiciel',
        apply: (state, params) => {
          investNow(state, 'monde', params.amount);
          state.knowledgePoints += 1;
          return { key: 'event.fonds_maison_conseiller.indiciel.outcome' };
        },
      },
      {
        id: 'refuser',
        label: 'event.fonds_maison_conseiller.refuser',
        apply: () => ({ key: 'event.fonds_maison_conseiller.refuser.outcome' }),
      },
    ],
  },
  {
    id: 'bulle_pixelune',
    kind: 'tentation',
    stages: ['stage2'],
    weight: 3,
    maxCount: 2,
    condition: (state) =>
      state.pea.open && state.market.regime === 'bulle' && changeOver(state, 'pixelune', 12) >= 0.3,
    title: 'event.bulle_pixelune.title',
    text: 'event.bulle_pixelune.text',
    params: (state) => ({
      gainPct: changeOver(state, 'pixelune', 12),
      cost: Math.round(10 * priceOf(state, 'pixelune') * 1.005),
    }),
    choices: [
      {
        id: 'acheter',
        label: 'event.bulle_pixelune.acheter',
        available: (state, params) => Math.max(0, state.cash) + state.pea.cash >= params.cost,
        unavailable: 'event.notEnoughMoney',
        apply: (state, params) => {
          depositToPea(state, Math.max(0, params.cost - state.pea.cash));
          buyShares(state, 'pixelune', 10);
          return { key: 'event.bulle_pixelune.acheter.outcome' };
        },
      },
      {
        id: 'vendre',
        label: 'event.bulle_pixelune.vendre',
        visible: (state) => (state.pea.holdings.pixelune?.shares ?? 0) > 0,
        apply: (state) => {
          sellShares(state, 'pixelune', state.pea.holdings.pixelune?.shares ?? 0);
          return { key: 'event.bulle_pixelune.vendre.outcome' };
        },
      },
      {
        id: 'plan',
        label: 'event.bulle_pixelune.plan',
        apply: () => ({ key: 'event.bulle_pixelune.plan.outcome' }),
      },
    ],
  },
  {
    id: 'krach_bourse',
    kind: 'marche',
    stages: ['stage2'],
    weight: 5,
    cooldownDays: DAYS_PER_YEAR,
    condition: (state) => hasMarketShares(state) && marketDrop(state) >= 0.25,
    title: 'event.krach_bourse.title',
    text: 'event.krach_bourse.text',
    params: (state) => ({ dropPct: marketDrop(state), value: sharesValue(state), deposit: 500 }),
    choices: [
      {
        id: 'vendre',
        label: 'event.krach_bourse.vendre',
        apply: (state) => {
          sellAllShares(state);
          return { key: 'event.krach_bourse.vendre.outcome' };
        },
      },
      {
        id: 'garder',
        label: 'event.krach_bourse.garder',
        apply: (state) => {
          state.flags.heldThroughDrop = true;
          return { key: 'event.krach_bourse.garder.outcome' };
        },
      },
      {
        id: 'acheter',
        label: 'event.krach_bourse.acheter',
        // Le versement va au PEA.
        visible: (state) => state.pea.open,
        apply: (state, params) => {
          state.flags.heldThroughDrop = true;
          investNow(state, state.pea.monthlyAsset, params.deposit);
          return { key: 'event.krach_bourse.acheter.outcome', params: { amount: fmtPct(marketDrop(state)) } };
        },
      },
    ],
  },
];

/** Les trois façons de répondre à des prélèvements rejetés. */
function rejetChoices(event: 'premier_rejet' | 'prelevements_rejetes'): EventChoice[] {
  const key = (suffix: string) => `event.${event}.${suffix}` as TextKey;
  return [
    {
      id: 'regulariser',
      label: key('regulariser'),
      available: (state) => regulatedSavings(state) >= depassement(state) + DECOUVERT.regularizeFees,
      unavailable: 'event.notEnoughSavings',
      apply: (state) => {
        coverOverdraftFromSavings(state);
        return { key: key('regulariser.outcome') };
      },
    },
    {
      id: 'appeler',
      label: key('appeler'),
      apply: (state) => {
        payFromCash(state, DECOUVERT.callFees);
        loseEnergy(state, 2);
        return { key: key('appeler.outcome') };
      },
    },
    {
      id: 'laisser',
      label: key('laisser'),
      apply: (state) => {
        payFromCash(state, DECOUVERT.rejectFees);
        return { key: key('laisser.outcome') };
      },
    },
  ];
}

function spiralCards(): EventDef[] {
  const ALL: StageId[] = ['prologue', 'stage1', 'stage2'];
  return [
    {
      id: 'premier_rejet',
      kind: 'imprevu',
      stages: ALL,
      weight: 0,
      manual: true,
      bad: true,
      shock: 5,
      condition: (state) => !state.surendettement && depassement(state) > rejectionThreshold(state),
      title: 'event.premier_rejet.title',
      text: 'event.premier_rejet.text',
      params: (state) => ({ dep: depassement(state), fees: DECOUVERT.rejectFees, reg: DECOUVERT.regularizeFees }),
      choices: rejetChoices('premier_rejet'),
    },
    {
      id: 'prelevements_rejetes',
      kind: 'imprevu',
      stages: ALL,
      weight: 0,
      manual: true,
      condition: (state) => !state.surendettement && depassement(state) > rejectionThreshold(state),
      title: 'event.prelevements_rejetes.title',
      text: 'event.prelevements_rejetes.text',
      params: (state) => ({ dep: depassement(state), fees: DECOUVERT.rejectFees, reg: DECOUVERT.regularizeFees }),
      choices: rejetChoices('prelevements_rejetes'),
    },
    {
      id: 'banque_convocation',
      kind: 'imprevu',
      stages: ALL,
      weight: 0,
      manual: true,
      bad: true,
      shock: 5,
      condition: (state) =>
        !state.surendettement &&
        !state.cardWithdrawn &&
        depassement(state) > 0 &&
        state.depassementMonths >= DECOUVERT.convocationMonths,
      title: 'event.banque_convocation.title',
      text: 'event.banque_convocation.text',
      params: (state) => ({ dep: depassement(state), payment: restructurePayment(state) }),
      choices: [
        {
          id: 'transformer',
          label: 'event.banque_convocation.transformer',
          apply: (state) => {
            const { payment, interest } = restructureOverdraft(state);
            return loanOutcome('event.banque_convocation.transformer.outcome', payment, interest);
          },
        },
        {
          id: 'refuser',
          label: 'event.banque_convocation.refuser',
          apply: (state) => {
            refuseConvocation(state);
            return { key: 'event.banque_convocation.refuser.outcome' };
          },
        },
      ],
    },
    {
      id: 'dossier_surendettement',
      kind: 'imprevu',
      stages: ALL,
      weight: 0,
      manual: true,
      bad: true,
      shock: 6,
      condition: (state) =>
        !state.surendettement &&
        depassement(state) > rejectionThreshold(state) &&
        regulatedSavings(state) < 100 &&
        dettesExigibles(state) > DECOUVERT.debtMonths * Math.max(500, monthlyIncome(state)),
      title: 'event.dossier_surendettement.title',
      text: 'event.dossier_surendettement.text',
      params: (state) => ({ debt: dettesExigibles(state), keep: DECOUVERT.planKeep }),
      choices: [
        {
          id: 'deposer',
          label: 'event.dossier_surendettement.deposer',
          apply: (state) => {
            startSurendettement(state);
            return { key: 'event.dossier_surendettement.deposer.outcome' };
          },
        },
        {
          id: 'attendre',
          label: 'event.dossier_surendettement.attendre',
          apply: () => ({ key: 'event.dossier_surendettement.attendre.outcome' }),
        },
      ],
    },
  ];
}

/** Payer les frais de scolarité en retard, ou emprunter pour le faire. */
function schoolPayChoices(event: 'ecole_impayee' | 'ecole_derniere_chance'): EventChoice[] {
  const key = (suffix: string) => `event.${event}.${suffix}` as TextKey;
  const settle = (state: GameState) => {
    state.flags = { ...state.flags, ecoleImpayee: false };
    addJournal(state, 'school.paid');
  };
  return [
    {
      id: 'payer',
      label: key('payer'),
      available: (state, params) => Math.max(0, state.cash) + regulatedSavings(state) >= (params.fees ?? 0),
      unavailable: 'event.notEnoughMoney',
      apply: (state, params) => {
        payFromSavings(state, params.fees ?? 0);
        settle(state);
        return { key: key('payer.outcome') };
      },
    },
    {
      id: 'emprunter',
      label: key('emprunter'),
      available: (state) => bankLoanRefusal(state, 'student', 5000, 60) === null,
      unavailable: (state) => (state.ficp ? 'event.ficpBloque' : 'event.banqueRefuse'),
      apply: (state, params) => {
        takeBankLoan(state, 'student', 5000, 60);
        payFromCash(state, params.fees ?? 0);
        settle(state);
        return { key: key('emprunter.outcome') };
      },
    },
  ];
}

function schoolUnpaidCards(): EventDef[] {
  return [
    {
      id: 'ecole_impayee',
      kind: 'imprevu',
      stages: ['prologue'],
      weight: 0,
      manual: true,
      bad: true,
      shock: 7,
      condition: (state) => inPrivateSchool(state) && state.flags.ecoleImpayee === true,
      title: 'event.ecole_impayee.title',
      text: 'event.ecole_impayee.text',
      params: (state) => ({ fees: atTodayPrices(state, SCHOOL_FEE) }),
      choices: [
        ...schoolPayChoices('ecole_impayee'),
        {
          id: 'reporter',
          label: 'event.ecole_impayee.reporter',
          apply: (state, params) => {
            scheduleFollowUp(state, 'ecole_derniere_chance', DECOUVERT.schoolDelayDays, { fees: params.fees ?? 0 });
            return { key: 'event.ecole_impayee.reporter.outcome' };
          },
        },
      ],
    },
    {
      id: 'ecole_derniere_chance',
      kind: 'imprevu',
      stages: ['prologue'],
      weight: 0,
      manual: true,
      bad: true,
      shock: 8,
      condition: (state) => inPrivateSchool(state) && state.flags.ecoleImpayee === true,
      title: 'event.ecole_derniere_chance.title',
      text: 'event.ecole_derniere_chance.text',
      params: (state) => ({ fees: atTodayPrices(state, SCHOOL_FEE) }),
      choices: [
        ...schoolPayChoices('ecole_derniere_chance'),
        {
          id: 'renoncer',
          label: 'event.ecole_derniere_chance.renoncer',
          apply: (state) => {
            const salary = excludeFromSchool(state);
            return { key: 'event.ecole_derniere_chance.renoncer.outcome', params: { salary: fmtEur(salary) } };
          },
        },
      ],
    },
  ];
}
